import { computed, inject, ref, watch } from "vue";
import { Store, storeKey, useStore } from "vuex";
import { getProperty } from "@/lib/helpers";

/**
 * Reads a tenant feature flag from the provided Vuex store, fetching the tenant
 * when it has not been loaded yet. Requires a store: a component without one
 * should use `useOptionalFeatureFlag`.
 */
export function useFeatureFlag(feature: string) {
  const store = useStore();

  if (!store.state.tenant && !store.state.fetchingTenant) {
    store.dispatch("fetchTenant");
  }

  const featureEnabled = computed(() => {
    if (!getProperty(store.state.tenant, "feature_flags") || !feature) {
      return false;
    } else {
      return store.state.tenant.feature_flags[feature];
    }
  });
  return featureEnabled;
}

interface TenantFlagsState {
  tenant?: { feature_flags?: Record<string, unknown> } | null;
}

/**
 * Reads a tenant feature flag when a store happens to be provided and reports
 * the flag off when none is. For shared components that also mount without
 * Vuex (Storybook, isolated specs, legacy hosts): the read never throws and
 * never triggers a tenant fetch. The tenant payload omits a disabled flag's
 * key entirely, so anything other than `true` means off.
 */
export function useOptionalFeatureFlag(feature: string) {
  const store = inject<Store<TenantFlagsState> | null>(storeKey, null);

  return computed(() => store?.state.tenant?.feature_flags?.[feature] === true);
}

/**
 * Reads a tenant feature flag and also says whether the answer is known yet.
 *
 * `useFeatureFlag` returns false both for "the flag is off" and for "the tenant
 * fetch has not answered", so a consumer keyed on it alone is guaranteed a
 * disabled-branch first paint that is then torn down. `resolved` is the third
 * state: true once feature flags are present, or once the fetch has settled
 * either way. `enabled` is only ever true once `resolved` is, so a consumer of
 * the enabled branch alone needs no extra guard; the disabled branch does.
 * Consumers whose two branches differ by more than a CSS class should render
 * neither until `resolved` is true. components/FeatureFlag.vue carries the
 * same gate for the same reason.
 */
export function useResolvedFeatureFlag(feature: string) {
  const store = useStore();
  // Starts the tenant fetch when nothing is loaded or in flight.
  const flag = useFeatureFlag(feature);

  // Tracks the fetch having SETTLED rather than succeeded: `fetchTenant` has no
  // rejection handler, so on a failed request `tenant` stays null forever and a
  // gate keyed on the payload alone would never open.
  const fetchSettled = ref(!!store.state.tenant);

  // `fetchTenant` clears `fetchingTenant` in a `finally`, so the falling edge is
  // the one signal that covers both outcomes — including for a caller that did
  // not start the request itself.
  watch(
    () => store.state.fetchingTenant,
    (isFetching, wasFetching) => {
      if (wasFetching && !isFetching) fetchSettled.value = true;
    }
  );

  const resolved = computed(
    () =>
      !!getProperty(store.state.tenant, "feature_flags") || fetchSettled.value
  );

  // The jbuilder view omits the key entirely when the flag is off, so a missing
  // key and an explicit false are the same answer.
  const enabled = computed(() => !!flag.value);

  return { enabled, resolved };
}
